import { useState, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatDate, titleCase } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  banned: boolean;
  createdAt: string;
  organizations: Array<{ id: string; name: string; role: string }>;
}

export function AdminUsersPage(): ReactNode {
  return <AdminLayout>{(staff) => <Users staff={staff} />}</AdminLayout>;
}

function Users({ staff }: { staff: { userId: string; role: string } }): ReactNode {
  const [search, setSearch] = useState('');
  const users = useAsync(
    () =>
      api.get<{ data: AdminUser[]; total: number }>(
        `/api/admin/users?limit=50&search=${encodeURIComponent(search)}`,
      ),
    [search],
  );
  const { run, pending, error } = useAction();

  const canWrite = staff.role === 'superadmin';

  if (users.loading && !users.data) return <Loading />;

  async function setRole(userId: string, role: string): Promise<void> {
    await run(async () => {
      await api.patch(`/api/admin/users/${userId}/role`, { role });
      users.reload();
    });
  }

  return (
    <div className="stack">
      <ErrorBanner error={users.error} />
      <ErrorBanner message={error} />

      <Card>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email"
          aria-label="Search users"
        />

        <table style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>User</th>
              <th>Organizations</th>
              <th>Platform role</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {(users.data?.data ?? []).map((entry) => (
              <tr key={entry.id}>
                <td>
                  <strong>{entry.name}</strong>
                  <div className="faint small">{entry.email}</div>
                </td>
                <td className="small muted">
                  {entry.organizations.length === 0
                    ? '—'
                    : entry.organizations.map((o) => `${o.name} (${o.role})`).join(', ')}
                </td>
                <td>
                  {canWrite ? (
                    <select
                      value={entry.role}
                      disabled={pending}
                      onChange={(e) => void setRole(entry.id, e.target.value)}
                      style={{ width: 'auto' }}
                    >
                      <option value="user">user</option>
                      <option value="support">support</option>
                      <option value="superadmin">superadmin</option>
                    </select>
                  ) : (
                    <span className="badge">{entry.role}</span>
                  )}
                  {entry.id === staff.userId && (
                    <span className="badge" style={{ marginLeft: '0.4rem' }}>
                      You
                    </span>
                  )}
                </td>
                <td className="muted small">{formatDate(entry.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {canWrite && (
        <p className="small faint">
          Granting `superadmin` gives full control of pricing and payment credentials across every
          tenant. You cannot remove your own access, and the last superadmin cannot be demoted.
        </p>
      )}
    </div>
  );
}

interface AuditEntry {
  id: string;
  action: string;
  organizationId: string | null;
  actorEmail: string | null;
  actorType: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export function AdminAuditPage(): ReactNode {
  return <AdminLayout>{() => <Audit />}</AdminLayout>;
}

function Audit(): ReactNode {
  const [action, setAction] = useState('');
  const logs = useAsync(
    () =>
      api.get<{ data: AuditEntry[]; total: number }>(
        `/api/admin/audit-logs?limit=100&action=${encodeURIComponent(action)}`,
      ),
    [action],
  );

  if (logs.loading && !logs.data) return <Loading />;

  return (
    <div className="stack">
      <ErrorBanner error={logs.error} />

      <Card>
        <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="audit-action">Filter by action prefix</label>
            <input
              id="audit-action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="admin.  ·  subscription.  ·  usage."
            />
          </div>
          {logs.loading && <Spinner />}
        </div>

        <table style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {(logs.data?.data ?? []).map((entry) => (
              <tr key={entry.id}>
                <td className="muted small">{formatDate(entry.createdAt)}</td>
                <td>
                  <span className="mono small">{entry.action}</span>
                </td>
                <td className="small">
                  {entry.actorEmail ?? <span className="faint">{titleCase(entry.actorType)}</span>}
                </td>
                <td className="small faint mono">
                  {entry.targetType ? `${entry.targetType} ${entry.targetId ?? ''}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(logs.data?.data.length ?? 0) === 0 && (
          <p className="muted small" style={{ marginTop: '0.75rem' }}>
            Nothing matches that filter.
          </p>
        )}
      </Card>
    </div>
  );
}
